import {isFeatureEnabled} from 'feature-flags';
import {Flag} from 'platform/components';
import {
  Box,
  Grid,
  Heading,
  HStack,
  Link,
  Scroll,
  Show,
  Text,
  useElementSize,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useEffect} from 'react';

import {isNil, isNotEmpty, min} from 'ramda';
import {floor, isFalse, isNotNil} from 'ramda-adjunct';

import {
  AuctionVehicleDetailResponseBody,
  useSourcingSetAuctionUserSeenMutation,
} from '@dms/api/core';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useAuctionEnumLookup} from '../../hooks/useAuctionEnumLookup';
import {CarStatusFilter} from '../../pages/AllOffers/types/CarStatusFilter';
import {buildAuctionVehicleTitle} from '../../utils/buildAuctionVehicleTitle';
import {resolveAuctionEnumLabel} from '../../utils/resolveAuctionEnumLabel';
import {Gallery} from '../VehiclePreview/components/Gallery';
import {VehicleDescription} from '../VehiclePreview/components/VehicleDescription';
import {VehicleSpecificationColumn} from '../VehiclePreview/components/VehicleSpecificationColumn';
import {AuctionCurrentBid} from './components/AuctionCurrentBid';
import {AuctionPickupPoint} from './components/AuctionPickupPoint';
import {AuctionPreviewRedesign} from './components/AuctionPreviewRedesign';
import {AuctionPricingGrid} from './components/AuctionPricingGrid';
import {AuctionServerLogo} from './components/AuctionServerLogo';
import {AuctionStatusHeader} from './components/AuctionStatusHeader';

interface AuctionPreviewProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
  /**
   * Lets the preview grow with its content instead of scrolling inside the viewport height, so the
   * page around it can do the scrolling.
   */
  isScrollDisabled?: boolean;
}

export function AuctionPreview(props: AuctionPreviewProps) {
  const auction = props.auction;
  const auctionVehicle = auction.auctionVehicle;
  const enumLookup = useAuctionEnumLookup();
  const formatNumber = useFormatNumber();
  const [ref, width] = useElementSize<HTMLDivElement>();
  const featureColumnsCount = isNil(width) ? 3 : min(floor(width / 200), 3);

  const [setAuctionUserSeen] = useSourcingSetAuctionUserSeenMutation();
  useEffect(
    function markAuctionAsSeen() {
      if (isFalse(auction.seen) && isNotNil(auction.auctionVehicle.adId)) {
        setAuctionUserSeen({body: {externalId: auction.auctionVehicle.adId}})
          .unwrap()
          .catch((error: ApiError) => handleApiError(error, {silent: true}));
      }
    },
    [auction, setAuctionUserSeen]
  );

  if (isFeatureEnabled(featureFlags.SOURCING_VEHICLE_PREVIEW_REDESIGN)) {
    return (
      <Box
        backgroundColor="palettes.neutral.20.100"
        height="100%"
        borderLeft="1px solid"
        borderColor="palettes.neutral.40.100"
        overflow="hidden"
      >
        <Scroll height={props.isScrollDisabled ? undefined : 'calc(100vh - 48px)'} auto>
          <Box padding={4}>
            <AuctionPreviewRedesign auction={auction} data-testid={props['data-testid']} />
          </Box>
        </Scroll>
      </Box>
    );
  }

  const hasPortalLink = isNotNil(auctionVehicle.url);
  const portalLink = auctionVehicle.url;

  const resolve = (value: string | null | undefined) => resolveAuctionEnumLabel(value, enumLookup);

  const featuresList =
    auction.auctionVehicle.features?.map((feature) =>
      resolveAuctionEnumLabel(feature, enumLookup)
    ) ?? [];

  const vehicleTitle = buildAuctionVehicleTitle(
    auctionVehicle.make,
    auctionVehicle.model,
    auctionVehicle.adTitle,
    enumLookup
  );

  return (
    <Box
      backgroundColor="general.white"
      height="100%"
      borderLeft="1px solid"
      borderColor="palettes.neutral.40.100"
      overflow="hidden"
      ref={ref}
    >
      <Scroll height={props.isScrollDisabled ? undefined : 'calc(100vh - 48px)'} auto>
        <Box padding={4}>
          <VStack spacing={4}>
            {/* Header: portal badge + open link */}
            <HStack justify="space-between" align="center">
              <Show when={isNotNil(auctionVehicle.seller)}>
                <AuctionServerLogo
                  server={auctionVehicle.seller?.id ?? ''}
                  serverName={auctionVehicle.seller?.name ?? undefined}
                  data-testid={suffixTestId('serverLogo', props)}
                />
              </Show>
              <Show when={isNil(auctionVehicle.seller) && isNotNil(auctionVehicle.auctionName)}>
                <Flag
                  label={auctionVehicle.auctionName ?? ''}
                  data-testid={suffixTestId('portalBadge', props)}
                />
              </Show>
              <Show when={hasPortalLink}>
                <Link
                  title={i18n.t('entity.auction.labels.openOriginalUrl')}
                  rightIcon="action/open_in_new"
                  size="small"
                  href={portalLink ?? undefined}
                  target="_blank"
                  data-testid={suffixTestId('openPortalLink', props)}
                />
              </Show>
            </HStack>

            {/* Vehicle title */}
            <Heading size={1} data-testid={suffixTestId('vehicleTitle', props)}>
              {vehicleTitle}
            </Heading>

            {/* Gallery */}
            <Gallery
              sourcingVehicle={{
                pictureUrls: auctionVehicle.picturesUrl ?? [],
                thumbnailUrl: {full: auctionVehicle.thumbnailUrl ?? null},
              }}
            />

            {/* Auction status + countdown */}
            <AuctionStatusHeader
              carEndTime={auctionVehicle.carEndTime}
              isEnded={auctionVehicle.carStatus === CarStatusFilter.ENDED}
              data-testid={suffixTestId('statusHeader', props)}
            />

            {/* Current bid */}
            <AuctionCurrentBid auction={auction} data-testid={suffixTestId('currentBid', props)} />

            {/* Place your bid – TODO show when we have data and tune styling */}
            {/* <AuctionBidSection auction={auction} data-testid={suffixTestId('bidSection', props)} /> */}

            {/* Buy now – TODO show when we have data and tune styling */}
            {/* <Show when={isNotNil(auction.priceBuyNow)}>
              <AuctionBuyNowSection auction={auction} data-testid={suffixTestId('buyNow', props)} />
            </Show> */}

            {/* Auction note – TODO show when we have data */}
            {/* <AuctionNote data-testid={suffixTestId('note', props)} /> */}

            {/* Pricing grid */}
            <AuctionPricingGrid auction={auction} data-testid={suffixTestId('pricing', props)} />

            {/* Pickup point */}
            <AuctionPickupPoint
              auction={auction}
              data-testid={suffixTestId('pickupPoint', props)}
            />

            {/* Specification */}
            <VStack spacing={4}>
              <Heading size={3}>
                {i18n.t('entity.sourcing.vehiclePreview.labels.specification')}
              </Heading>
              <HStack spacing={4} wrap>
                <Show when={isNotNil(auctionVehicle.year)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.vehicle.labels.year')}
                    icon="automotive/calendar"
                    value={
                      isNotNil(auctionVehicle.year)
                        ? String(auctionVehicle.year)
                        : EMPTY_PLACEHOLDER
                    }
                  />
                </Show>
                <Show when={isNotNil(auctionVehicle.power)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.sourcing.vehiclePreview.labels.power')}
                    icon="automotive/engine"
                    value={
                      isNotNil(auctionVehicle.power)
                        ? `${auctionVehicle.power} ${i18n.t('general.metric.kW')}`
                        : EMPTY_PLACEHOLDER
                    }
                  />
                </Show>
                <Show when={isNotNil(auctionVehicle.driveType)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.vehicle.labels.driveType')}
                    icon="automotive/differential"
                    value={resolve(auctionVehicle.driveType) ?? EMPTY_PLACEHOLDER}
                  />
                </Show>
                <VehicleSpecificationColumn
                  label={i18n.t('entity.sourcing.vehiclePreview.labels.mileage')}
                  icon="automotive/mileage"
                  value={
                    isNotNil(auctionVehicle.mileage)
                      ? `${formatNumber(auctionVehicle.mileage)} ${i18n.t('general.metric.km')}`
                      : EMPTY_PLACEHOLDER
                  }
                />
                <Show when={isNotNil(auctionVehicle.transmission)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.sourcing.vehiclePreview.labels.transmission')}
                    icon="automotive/transmission"
                    value={resolve(auctionVehicle.transmission) ?? EMPTY_PLACEHOLDER}
                  />
                </Show>
                <Show when={isNotNil(auctionVehicle.fuelType)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.sourcing.vehiclePreview.labels.fuel')}
                    icon="automotive/fuel_type"
                    value={resolve(auctionVehicle.fuelType) ?? EMPTY_PLACEHOLDER}
                  />
                </Show>
                <Show when={isNotNil(auctionVehicle.carStyle)}>
                  <VehicleSpecificationColumn
                    label={i18n.t('entity.sourcing.vehiclePreview.labels.bodyType')}
                    icon="automotive/car"
                    value={resolve(auctionVehicle.carStyle) ?? EMPTY_PLACEHOLDER}
                  />
                </Show>
              </HStack>
            </VStack>

            {/* Vehicle features */}
            <Show when={isNotEmpty(featuresList)}>
              <VStack spacing={4}>
                <Heading size={3}>
                  {i18n.t('entity.sourcing.vehiclePreview.labels.vehicleFeatures')}
                </Heading>
                <Grid columns={featureColumnsCount} spacing={2}>
                  {featuresList.map((feature) => (
                    <Text key={feature} size="small">
                      {feature}
                    </Text>
                  ))}
                </Grid>
              </VStack>
            </Show>

            {/* Vehicle description */}
            <Show when={isNotNil(auctionVehicle.description)}>
              <VStack spacing={4}>
                <Heading size={3}>
                  {i18n.t('entity.sourcing.vehiclePreview.labels.vehicleDescription')}
                </Heading>
                <VehicleDescription
                  description={auctionVehicle.description}
                  data-testid={suffixTestId('vehicleDescription', props)}
                />
              </VStack>
            </Show>
          </VStack>
        </Box>
      </Scroll>
    </Box>
  );
}
